import { MenuItem, MenuItemDetail, SizePrice } from '../services/restaurantService';
import { getModifierGroups, itemHasOptions } from './menuOptions';

export function roundDisplayPrice(price: number | undefined | null): number {
  if (price === undefined || price === null || Number.isNaN(Number(price))) {
    return 0;
  }
  return Math.round(Number(price));
}

export function getAvailableSizes(
  item: Pick<MenuItem | MenuItemDetail, 'sizePrices'>
): SizePrice[] {
  const sizes = item.sizePrices || [];
  return sizes.filter((s) => s.isAvailable !== false);
}

export function needsSizeSelection(
  item: Pick<MenuItem | MenuItemDetail, 'sizePrices'>
): boolean {
  return getAvailableSizes(item).length > 1;
}

export function getDefaultSizeTypeId(
  item: Pick<MenuItem | MenuItemDetail, 'sizePrices'> & { defaultSizeTypeId?: number }
): number | undefined {
  const sizes = getAvailableSizes(item);
  if (sizes.length === 0) {
    return item.defaultSizeTypeId ?? undefined;
  }
  // Server default (menu_size_price.is_default), when it is on sale.
  if (item.defaultSizeTypeId != null) {
    const preferred = sizes.find((s) => s.sizeTypeId === item.defaultSizeTypeId);
    if (preferred) {
      return preferred.sizeTypeId;
    }
  }
  const flagged = sizes.find((s) => s.isDefault === true);
  if (flagged) {
    return flagged.sizeTypeId;
  }
  const regular = sizes.find(
    (s) => s.sizeTypeCode && s.sizeTypeCode.toUpperCase() === 'REGULAR'
  );
  return (regular || sizes[0]).sizeTypeId;
}

export function getSizePrice(
  item: Pick<MenuItem | MenuItemDetail, 'sizePrices'>,
  sizeTypeId?: number
): SizePrice | undefined {
  const sizes = getAvailableSizes(item);
  if (sizes.length === 0) {
    return undefined;
  }
  if (sizeTypeId != null) {
    return sizes.find((s) => s.sizeTypeId === sizeTypeId) || sizes[0];
  }
  return sizes.find((s) => s.sizeTypeId === getDefaultSizeTypeId(item)) || sizes[0];
}

export function getDisplayPrice(
  item: Pick<MenuItem | MenuItemDetail, 'price' | 'sellingPrice' | 'sizePrices'>,
  options?: { sizeTypeId?: number }
): number {
  const size = getSizePrice(item, options?.sizeTypeId);
  if (size) {
    return roundDisplayPrice(size.sellingPrice ?? size.price);
  }
  return roundDisplayPrice(item.sellingPrice ?? item.price);
}

type PromotionalPriceFields = {
  promotionalPrice?: number;
  promotionalPercentOff?: number;
};

/** True when `sizeTypeId` is (or falls back to) the item's default variant. */
function isDefaultVariant(
  item: Pick<MenuItem | MenuItemDetail, 'sizePrices'> & { defaultSizeTypeId?: number },
  sizeTypeId?: number
): boolean {
  if (sizeTypeId == null) return true;
  const sizes = getAvailableSizes(item);
  if (sizes.length === 0) return true;
  const chosen = getSizePrice(item, sizeTypeId);
  return chosen == null || chosen.sizeTypeId === getDefaultSizeTypeId(item);
}

/**
 * Per-unit price of a variant as billed: item-level menu discount and a % promotion (the server
 * takes the % off the line subtotal, so it is the same on every unit). A flat promotion is NOT
 * taken off: the server applies it once per bill (PromotionServiceImpl.computePromotionDiscount),
 * not per unit or per variant, so it is shown as a separate offer hint instead.
 */
export function getVariantUnitPrice(
  item: Pick<MenuItem | MenuItemDetail, 'price' | 'sellingPrice' | 'sizePrices'> & PromotionalPriceFields,
  options?: { sizeTypeId?: number }
): number {
  const sellingPrice = getDisplayPrice(item, options);
  if (item.promotionalPercentOff != null && item.promotionalPercentOff > 0) {
    return roundDisplayPrice(sellingPrice * (1 - item.promotionalPercentOff / 100));
  }
  return sellingPrice;
}

/** A flat (once per bill) promotion is advertised for this item. */
export function hasFlatPromotion(item: PromotionalPriceFields): boolean {
  return (
    !(item.promotionalPercentOff != null && item.promotionalPercentOff > 0) &&
    item.promotionalPrice != null &&
    item.promotionalPrice >= 0
  );
}

/**
 * Menu/catalog price after item-level menu discounts and applicable flat/% promotions. The
 * server computes `promotionalPrice` from the default variant only, so it is applied to that
 * variant only; other variants get the % promotion (if any) via getVariantUnitPrice.
 */
export function getEffectiveDisplayPrice(
  item: Pick<MenuItem | MenuItemDetail, 'price' | 'sellingPrice' | 'sizePrices'> &
    PromotionalPriceFields & { defaultSizeTypeId?: number },
  options?: { sizeTypeId?: number }
): number {
  const unitPrice = getVariantUnitPrice(item, options);
  if (item.promotionalPercentOff != null && item.promotionalPercentOff > 0) {
    return unitPrice;
  }
  if (
    item.promotionalPrice != null &&
    item.promotionalPrice >= 0 &&
    item.promotionalPrice < unitPrice &&
    isDefaultVariant(item, options?.sizeTypeId)
  ) {
    return roundDisplayPrice(item.promotionalPrice);
  }
  return unitPrice;
}

/** Strikethrough reference price before promotion savings (MRP when higher than selling). */
export function getStrikeThroughPrice(
  item: Pick<MenuItem | MenuItemDetail, 'mrp' | 'price' | 'sellingPrice' | 'sizePrices'> &
    PromotionalPriceFields & { defaultSizeTypeId?: number },
  options?: { sizeTypeId?: number }
): number | undefined {
  const sellingPrice = getDisplayPrice(item, options);
  const effectivePrice = getEffectiveDisplayPrice(item, options);
  const mrp = getDisplayMrp(item, options?.sizeTypeId);
  const base = mrp != null && mrp > sellingPrice ? mrp : sellingPrice;
  if (base > effectivePrice) {
    return base;
  }
  return undefined;
}

export function getDisplayMrp(
  item: Pick<MenuItem | MenuItemDetail, 'mrp' | 'price' | 'sizePrices'>,
  sizeTypeId?: number
): number | undefined {
  const size = getSizePrice(item, sizeTypeId);
  if (size?.mrp != null && size.mrp > 0) {
    return roundDisplayPrice(size.mrp);
  }
  if (item.mrp != null && item.mrp > 0) {
    return roundDisplayPrice(item.mrp);
  }
  return undefined;
}

type OptionsMetaItem = Pick<
  MenuItem | MenuItemDetail,
  'sizePrices' | 'variantLabel' | 'defaultSizeTypeId' | 'modifierGroups' | 'hasOptions'
>;

/** Adding the item needs the options sheet (add-on groups or more than one variant). */
export function needsOrderOptions(item: OptionsMetaItem): boolean {
  return itemHasOptions(item);
}

export interface MenuOptionsMeta {
  sizeCount: number;
  showOptionsLabel: boolean;
  /** Item has add-on groups ("Customisable" badge). */
  showCustomizable: boolean;
  needsPicker: boolean;
}

export function getMenuOptionsMeta(item: OptionsMetaItem): MenuOptionsMeta {
  const sizeCount = getAvailableSizes(item).length;
  return {
    sizeCount,
    showOptionsLabel: sizeCount > 1,
    showCustomizable: getModifierGroups(item).length > 0,
    needsPicker: needsOrderOptions(item),
  };
}
